
export const MAX_IMPORT_FILES = 5
export const MAX_IMPORT_FILE_SIZE_BYTES = 10 * 1024 * 1024
export const MAX_IMPORT_PDF_PAGES = 50
export const MAX_IMPORTS_PER_TRIP_PER_DAY = 20

export const ALLOWED_IMPORT_MIME_TYPES = [
	'image/jpeg',
	'image/png',
	'image/gif',
	'image/webp',
	'image/heic',
	'application/pdf',
	'text/plain',
] as const

export interface FileEntry {
	id: string
	file: File
	pageCount?: number
	error?: string
}

export const formatBytes = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Moved to the shared MIME home; re-exported so existing importers keep working.
export { isImageMime } from '@lib/mime'

export const isPdfMime = (mime: string): boolean =>
	mime === 'application/pdf'

const ALLOWED = ALLOWED_IMPORT_MIME_TYPES as readonly string[]

export const validateFile = (
	file: File,
	indexInList: number,
	pageCount: number | undefined,
	currentCount: number,
): string | undefined => {
	if (!ALLOWED.includes(file.type)) {
		return 'Unsupported file type'
	}
	if (currentCount + indexInList >= MAX_IMPORT_FILES) {
		return `${currentCount + indexInList + 1}th file — only ${MAX_IMPORT_FILES} allowed`
	}
	if (file.size > MAX_IMPORT_FILE_SIZE_BYTES) {
		return 'Over 10 MB cap'
	}
	if (file.type === 'application/pdf' && pageCount && pageCount > MAX_IMPORT_PDF_PAGES) {
		return `Has ${pageCount} pages — limit is ${MAX_IMPORT_PDF_PAGES}`
	}
	return undefined
}

const PDF_PAGE_RE = /\/Type[\s]*\/Page[^s]/g

/**
 * Best-effort PDF page count without pulling pdf-lib. Reads the first ~2MB
 * of the file and counts /Type /Page occurrences. Falls back to undefined.
 * The server is the source of truth for the real page count.
 */
export const countPdfPages = async (file: File): Promise<number | undefined> => {
	if (file.type !== 'application/pdf') return undefined
	try {
		const slice = file.slice(0, Math.min(file.size, 2 * 1024 * 1024))
		const buf = await slice.arrayBuffer()
		const text = new TextDecoder('latin1').decode(buf)
		const matches = text.match(PDF_PAGE_RE)
		return matches ? matches.length : undefined
	} catch {
		return undefined
	}
}

export const formatRetryAfter = (seconds: number): string => {
	const total = Math.max(0, Math.floor(seconds))
	const hours = Math.floor(total / 3600)
	const minutes = Math.floor((total % 3600) / 60)
	if (hours <= 0) return `${minutes} m`
	return `${hours} h ${minutes} m`
}

/**
 * Whether the events list can be trusted to show the import's result. It can
 * once every created event is in it — or once it has been refetched since the
 * import finished (`dataUpdatedAt` past the invalidation), in which case any
 * created event still missing was deleted on purpose and the import reads as
 * cleared rather than as still loading.
 */
export const importedEventsSettled = ({
	createdEventIds,
	events,
	dataUpdatedAt,
	invalidatedAt,
}: {
	createdEventIds: number[]
	events: ReadonlyArray<{ id: number }> | undefined
	/** react-query's timestamp for the events data; 0 while there is none. */
	dataUpdatedAt: number
	/** When this page invalidated the events for this import; null until it has. */
	invalidatedAt: number | null
}): boolean => {
	if (!events) return false
	if (createdEventIds.every(id => events.some(event => event.id === id))) return true
	return invalidatedAt !== null && dataUpdatedAt >= invalidatedAt
}
